import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '../lib/api'

export const useParticipantsStore = defineStore('participants', () => {
  const participants = ref([])
  const loading = ref(false)

  const pending = computed(() => participants.value.filter(p => !p.check))
  const confirmed = computed(() => participants.value.filter(p => p.check))
  const totalCount = computed(() => participants.value.length)
  const pendingCount = computed(() => pending.value.length)
  const confirmedCount = computed(() => confirmed.value.length)
  const checkInPercentage = computed(() => {
    if (totalCount.value === 0) return 0
    return Math.round((confirmedCount.value / totalCount.value) * 100)
  })

  async function fetchAll() {
    loading.value = true
    try {
      const response = await api.get('/listar?all=true')
      participants.value = response.data.users
    } finally {
      loading.value = false
    }
  }

  async function create(data) {
    const response = await api.post('/cadastrar', data)
    participants.value = [...participants.value, response.data.newUser]
    return response.data
  }

  function buildUpdatePayload(data, { newPhotos = [], removedPublicIds = [] } = {}) {
    if (newPhotos.length === 0 && removedPublicIds.length === 0) {
      return { body: data, config: undefined }
    }
    const formData = new FormData()
    Object.entries(data).forEach(([key, value]) => {
      if (value === undefined || value === null) return
      formData.append(key, typeof value === 'boolean' ? String(value) : value)
    })
    if (removedPublicIds.length > 0) {
      formData.append('removePhotoIds', JSON.stringify(removedPublicIds))
    }
    newPhotos.forEach(file => formData.append('baggagePhotos', file))
    return { body: formData, config: undefined }
  }

  async function update(id, data, photos) {
    const { body } = buildUpdatePayload(data, photos)
    const response = await api.patch(`/atualizar/${id}`, body)
    participants.value = participants.value.map(p =>
      p.id === id ? { ...p, ...response.data.updatedUser } : p
    )
    return response.data
  }

  async function checkin(id, extra = {}, photos) {
    // Usa /scan/:id pra que voluntarios CHECKIN tambem possam confirmar.
    const { body } = buildUpdatePayload(extra, photos)
    const response = await api.post(`/scan/${id}`, body)
    const updated = response.data.participant
    participants.value = participants.value.map(p =>
      p.id === id ? { ...p, ...updated } : p
    )
    return response.data
  }

  async function removeCheckin(id) {
    const response = await api.patch(`/atualizar/${id}`, { check: false })
    participants.value = participants.value.map(p =>
      p.id === id ? { ...p, ...response.data.updatedUser } : p
    )
    return response.data
  }

  async function remove(id) {
    await api.delete(`/remover/${id}`)
    participants.value = participants.value.filter(p => p.id !== id)
  }

  function addFromPusher(participant) {
    const exists = participants.value.some(p => p.id === participant.id)
    if (!exists) {
      participants.value = [...participants.value, participant]
    }
  }

  function removeFromPusher(id) {
    participants.value = participants.value.filter(p => p.id !== id)
  }

  function updateFromPusher(participant) {
    participants.value = participants.value.map(p =>
      p.id === participant.id ? { ...p, ...participant } : p
    )
  }

  return {
    participants, loading,
    pending, confirmed,
    totalCount, pendingCount, confirmedCount, checkInPercentage,
    fetchAll, create, update, checkin, removeCheckin, remove,
    addFromPusher, removeFromPusher, updateFromPusher
  }
})
